import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useGetUsers } from '@/hooks/api/use-get-users';
import { useGetClinics } from '@/hooks/api/use-get-clinics';
import { updateUser, UpdateUserData } from '@/api/users';
import { toast_success } from '@/libs/toast-wrappers';
import { ROLES, ROLES_LABELS } from '@/utils/constants';

import { InputField } from '@/components/form/input-field';
import { SelectField } from '@/components/form/select-field';
import { Button } from '@/components/ui/button';
import { FetchStates } from '@/components/fetch-states';
import { Hr } from '@/components/ui/divider';

import { ApiKeyManagement } from './api-key-management';

const createSchema = (t: TFunction) =>
  z.object({
    email: z.string(),
    full_name: z.string().trim().min(1, t('input-required')),
    contact_phone: z.string().trim(),
    role: z.string().trim().min(1, t('input-required')),
    clinic_id: z.string().trim().min(1, t('input-required')),
  });

type UserEditFormData = z.infer<ReturnType<typeof createSchema>>;

export function UserEditForm() {
  const { userId } = useParams(); // userId is the user email
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { users, loading, error, refetch } = useGetUsers();
  const { clinics, loading: clinicsLoading } = useGetClinics();

  const { onFormClose } = useOutletContext<{
    onFormClose: () => Promise<void>;
  }>();

  const schema = useMemo(() => createSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<UserEditFormData>({ resolver: zodResolver(schema) });

  // Find the user by email
  const user = users.find((u) => u.user_email === userId);

  // Transform clinics data to SelectField format: [{ clinic_id: clinic_name }]
  const clinicOptions =
    clinics?.map((clinic) => ({
      [clinic.clinic_id ?? '']: clinic.clinic_name ?? 'Unknown Clinic',
    })) ?? [];

  // Fill form with user data
  const fillForm = useCallback(() => {
    if (!user) return;

    setValue('email', user.user_email);
    setValue('full_name', user.full_name ?? '');
    setValue('contact_phone', user.contact_phone ?? '');
    setValue('role', user.role ?? '');
    setValue('clinic_id', user.clinic_id ?? '');
  }, [user, setValue]);

  useEffect(() => {
    fillForm();
  }, [fillForm]);

  const onSubmitEdit: SubmitHandler<UserEditFormData> = async (data) => {
    if (!userId) return;

    setIsSubmitting(true);
    try {
      const updateData: UpdateUserData = {
        email: data.email,
        full_name: data.full_name,
        contact_phone: data.contact_phone,
        role: data.role as ROLES,
        clinic_id: data.clinic_id,
      };

      await updateUser(userId, updateData);
      toast_success(t('admin-users-edit-success'));
      await onFormClose();
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    await onFormClose();
  };

  return (
    <FetchStates
      loading={loading}
      error={error?.message}
      refetch={() => void refetch()}
      empty={!user}
    >
      <form onSubmit={handleSubmit(onSubmitEdit)} className="w-full my-6 mx-4 flex flex-col gap-6">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-2">
          {/* Email - Disabled */}
          <InputField
            label={t('admin-users-table-email')}
            type="email"
            disabled
            classNameFieldSet="lg:col-span-2"
            {...register('email')}
          />

          {/* Full Name */}
          <InputField
            label={t('admin-users-edit-name-label')}
            required
            autoComplete="name"
            classNameFieldSet="lg:col-span-2"
            type="text"
            disabled={isSubmitting || clinicsLoading}
            errorMessage={errors.full_name?.message}
            {...register('full_name')}
          />

          {/* Phone */}
          <InputField
            label={t('admin-users-edit-phone-label')}
            autoComplete="tel"
            type="tel"
            disabled={isSubmitting || clinicsLoading}
            errorMessage={errors.contact_phone?.message}
            {...register('contact_phone')}
          />

          {/* Role */}
          <SelectField
            label={t('admin-users-edit-role-label')}
            required
            options={ROLES_LABELS}
            enableEmptyOption
            disabled={isSubmitting || clinicsLoading}
            errorMessage={errors.role?.message}
            {...register('role')}
          />

          {/* Clinic Selection */}
          <SelectField
            label={t('admin-users-edit-clinic-label')}
            required
            options={clinicOptions}
            enableEmptyOption
            classNameFieldSet="lg:col-span-2"
            disabled={isSubmitting || clinicsLoading}
            errorMessage={errors.clinic_id?.message}
            {...register('clinic_id')}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-4">
          <Button type="submit" loading={isSubmitting} disabled={clinicsLoading}>
            {t('admin-users-edit-save-button')}
          </Button>
          <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            {t('default-cancel')}
          </Button>
        </div>
      </form>

      {/* API Keys Management Section */}
      {userId && (
        <>
          <Hr className="my-8" />
          <div className="mx-4">
            <ApiKeyManagement userEmail={userId} />
          </div>
        </>
      )}
    </FetchStates>
  );
}
